import { WorkerPersonnelRequestActions, type StaffingOperationsPort } from '../../staffing/public';
import { workflowPersonnelRequestTarget } from '../models/workflowPersonnelRequestTarget';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AddOutlined,
  ChevronRightOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type {
  CollectionResponse,
  WorkflowRequest,
  WorkflowTemplate,
} from '../model';
import type {
  WorkflowPort,
  WorkflowRoutePort,
} from '../port';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { registerNavigationGuard } from '../../../shared/navigationGuard';
import {
  CommonFilterBar,
  createFocusRingStyles,
  designTokens,
  FilterTabs,
  FeedbackSnackbar,
  InteractiveCard,
  GuidePage,
  FormDialog,
  FormTextField,
  ResourceState,
  ResultState,
  ValueBadge,
} from '../../../design-system';
import type { WorkflowApprovalSettingsPort } from '../approval-settings/public';
import type { EmployeeDirectoryPort } from '../../people/public';
import {
  resolveWorkflowTemplateIconKey,
  resolveWorkflowTemplateIconVisual,
  workflowTemplateIconVisual,
} from '../workflowTemplateIcons';
import {
  fixtureRequests,
  fixtureTemplates,
} from '../fixtures/workflowPageFixtures';
import { submitFixtureWorkflowRequest } from '../fixtures/workflowRequestFixtureSupport';
import { RequestStatus, statusLabel } from '../components/WorkflowStatus';
import {
  isAmbiguousApiMutationFailure,
  type WorkflowAccessProps,
  workflowErrorCode,
} from '../workflowPageSupport';
import {
  formatWorkflowDate,
  formatWorkflowDateKey,
  formatWorkflowDateTime,
  type WorkflowComposerPresentation,
  workflowRequestIDFromLocation,
  workflowRequestLoadingPlaceholder,
  workflowRequestTitle,
  type WorkflowSubmitIntent,
} from '../models/workflowRequestPageModel';
import { isEditableWorkflowDraft, workflowRequestDisplayStatus } from '../models/workflowRequestListModel';
import { WorkflowRequestComposer } from '../components/WorkflowRequestComposer';
import { WorkflowRequestDocumentWorkspace } from '../components/WorkflowRequestDocumentWorkspace';

type RequestStatusFilter = 'ALL' | 'DRAFT' | 'NEEDS_CHANGES' | 'IN_PROGRESS' | 'AWAITING_EFFECTIVE' | 'EXECUTION_UNVERIFIED' | 'COMPLETED' | 'DENIED' | 'CANCELLED' | 'CLOSED';
type WorkflowRequestCenterView = 'create' | 'requests';
type RequestListContext = { status: RequestStatusFilter; search: string; filters: { templateId: string; dateFrom: string; dateTo: string } };

function workflowComposerDraftIDFromLocation(): string | undefined {
  const draftID = new URLSearchParams(window.location.search).get('draft')?.trim();
  return draftID || undefined;
}

function workflowComposerPresentationFromLocation(): WorkflowComposerPresentation {
  return new URLSearchParams(window.location.search).get('composer') === 'fullscreen' ? 'standalone' : 'dialog';
}

function workflowComposerPath(route: WorkflowRoutePort, draftID: string | undefined, presentation: WorkflowComposerPresentation): string {
  const search = new URLSearchParams();
  if (draftID) search.set('draft', draftID);
  if (presentation === 'standalone') search.set('composer', 'fullscreen');
  return route.path('my-workflows', search.size ? search : undefined);
}

const requestStatusFilters: Array<{ value: RequestStatusFilter; label: string }> = [
  { value: 'ALL', label: '전체' },
  { value: 'DRAFT', label: '작성 중' },
  { value: 'NEEDS_CHANGES', label: '보완 필요' },
  { value: 'IN_PROGRESS', label: '진행 중' },
  { value: 'AWAITING_EFFECTIVE', label: '시행 대기' },
  { value: 'EXECUTION_UNVERIFIED', label: '반영 확인 필요' },
  { value: 'COMPLETED', label: '완료' },
  { value: 'DENIED', label: '반려' },
  { value: 'CANCELLED', label: '취소' },
];

function workflowRequestStatusFromLocation(): RequestStatusFilter {
  const requestedStatus = new URLSearchParams(window.location.search).get('status');
  return requestedStatus === 'CLOSED' || requestStatusFilters.some((filter) => filter.value === requestedStatus)
    ? requestedStatus as RequestStatusFilter
    : 'ALL';
}

function workflowRequestRouteSearch(status: RequestStatusFilter, requestID?: string, searchText = '', filters?: { templateId: string; dateFrom: string; dateTo: string }): URLSearchParams | undefined {
  const search = new URLSearchParams();
  if (status !== 'ALL') search.set('status', status);
  if (searchText.trim()) search.set('search', searchText);
  if (requestID) search.set('request', requestID);
  if (filters) for (const key of ['templateId', 'dateFrom', 'dateTo'] as const) if (filters[key]) search.set(key, filters[key]);
  return search.size ? search : undefined;
}

export type WorkflowRequestCenterPageProps = {
  port: WorkflowPort;
  staffingPort?: StaffingOperationsPort;
  route: WorkflowRoutePort;
  approvalSettingsPort: WorkflowApprovalSettingsPort;
  directoryPort: EmployeeDirectoryPort;
  fixtureMode: boolean;
  view: WorkflowRequestCenterView;
  requesterWorkerId?: string;
  onRequestsChanged?: () => void;
  allowNewRequests?: boolean;
} & WorkflowAccessProps;

export function WorkflowRequestCenterPage({ port, staffingPort, route, approvalSettingsPort, directoryPort, fixtureMode, readOnly, scopes, view: activeView, requesterWorkerId, onRequestsChanged, allowNewRequests = true }: WorkflowRequestCenterPageProps) {
  const canReadTemplates = allowNewRequests && (fixtureMode || scopes.includes('workflow.template.read'));
  const canReadRequests = fixtureMode || scopes.includes('workflow.request.read.self');
  const hasCreatePermission = allowNewRequests && !readOnly && scopes.includes('workflow.request.create');
  const canCreate = hasCreatePermission && canReadRequests;
  const canCancel = !readOnly && scopes.includes('workflow.request.cancel');
  const canCancelTimeOff = !readOnly && scopes.includes('absence.write');
  const canManagePersonnel = !readOnly && (fixtureMode || scopes.includes('staffing.write'));
  const canCancelPersonnel = canManagePersonnel && (fixtureMode || scopes.includes('business_process.cancel'));
  const [personnelBusy, setPersonnelBusy] = useState(false);
  const [fixtureRequestItems, setFixtureRequestItems] = useState(() => structuredClone(fixtureRequests));
  const rememberFixtureRequest = (request: WorkflowRequest) => {
    setFixtureRequestItems((current) => [request, ...current.filter((item) => item.id !== request.id)]);
  };
  const templates = useAsyncResource(
    () => canReadTemplates
      ? (fixtureMode ? Promise.resolve(fixtureTemplates) : port.listWorkflowTemplates().then((response) => response.data))
      : Promise.resolve([] as WorkflowTemplate[]),
    [canReadTemplates, port, fixtureMode],
  );
  const requests = useAsyncResource<CollectionResponse<WorkflowRequest>>(
    () => canReadRequests
      ? (fixtureMode ? Promise.resolve({ total: fixtureRequestItems.length, data: fixtureRequestItems }) : port.listWorkflowRequests())
      : Promise.resolve({ total: 0, data: [] as WorkflowRequest[] }),
    [canReadRequests, port, fixtureMode, fixtureRequestItems],
  );
  const [templateQuery, setTemplateQuery] = useState('');
  const [category, setCategory] = useState('');
  const [requestQuery, setRequestQuery] = useState(() => new URLSearchParams(window.location.search).get('search') ?? '');
  const [requestStatus, setRequestStatus] = useState<RequestStatusFilter>(workflowRequestStatusFromLocation);
  const [requestFilters, setRequestFilters] = useState(() => ({ templateId: new URLSearchParams(window.location.search).get('templateId') ?? '', dateFrom: new URLSearchParams(window.location.search).get('dateFrom') ?? '', dateTo: new URLSearchParams(window.location.search).get('dateTo') ?? '' }));
  const [selectedPresentation, setSelectedPresentation] = useState<WorkflowComposerPresentation>(() => new URLSearchParams(window.location.search).get('presentation') === 'fullscreen' ? 'standalone' : 'dialog');
  const [selectedCommentDirty, setSelectedCommentDirty] = useState(false);
  const [selectedCommentBusy, setSelectedCommentBusy] = useState(false);
  const skipDetailGuard = useRef(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelComment, setCancelComment] = useState('');
  const cancelIntent = useRef<{ requestId: string; comment: string; key: string } | undefined>(undefined);
  const [copySaving, setCopySaving] = useState(false);
  const copyIntent = useRef<{ requestId: string; key: string } | undefined>(undefined);
  const initialComposerDraftID = workflowComposerDraftIDFromLocation();
  const initialRequestID = workflowRequestIDFromLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const [composerPresentation, setComposerPresentation] = useState<WorkflowComposerPresentation>(workflowComposerPresentationFromLocation);
  const [createTemplateId, setCreateTemplateId] = useState<string>();
  const [detailRetry, setDetailRetry] = useState(0);
  const [resumeDraftID, setResumeDraftID] = useState<string>();
  const [selectedRequest, setSelectedRequest] = useState<WorkflowRequest>();
  const [editingRequest, setEditingRequest] = useState<WorkflowRequest>();
  const [actionError, setActionError] = useState('');
  const [requestNotice, setRequestNotice] = useState('');
  const [submittedResult, setSubmittedResult] = useState<{ requestId?: string }>();
  const [requestWarning, setRequestWarning] = useState('');
  const [requestCompletionSequence, setRequestCompletionSequence] = useState(0);
  const [requestDetailStatus, setRequestDetailStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [routeDraftID, setRouteDraftID] = useState<string | undefined>(initialComposerDraftID);
  const [routeRequestID, setRouteRequestID] = useState<string | undefined>(initialRequestID);
  const [draftRestoreStatus, setDraftRestoreStatus] = useState<'idle' | 'loading' | 'error'>(initialComposerDraftID ? 'loading' : 'idle');
  const [draftRestoreError, setDraftRestoreError] = useState('');
  const [draftRestoreRetry, setDraftRestoreRetry] = useState(0);
  const detailRequestSequence = useRef(0);
  const draftRestoreSequence = useRef(0);
  const activeComposerDraftID = useRef<string | undefined>(undefined);
  const composerReturnContext = useRef<RequestListContext | undefined>(undefined);
  const requestCompletionTarget = useRef<WorkflowRequestCenterView | undefined>(undefined);
  const detailSubmitIntentRef = useRef<WorkflowSubmitIntent | undefined>(undefined);
  const [detailSubmitSaving, setDetailSubmitSaving] = useState(false);
  const [detailCancelSaving, setDetailCancelSaving] = useState(false);
  const detailActionPending = useRef(false);
  const selectedRequestID = useRef<string | undefined>(undefined);
  selectedRequestID.current = selectedRequest?.id;
  const isCurrentDetail = (id: string, sequence: number) => detailRequestSequence.current === sequence
    && selectedRequestID.current === id && route.currentPage() === 'my-workflow-requests'
    && workflowRequestIDFromLocation() === id;
  const [detailSubmitRetryPending, setDetailSubmitRetryPending] = useState(false);
  const availableTemplates = useMemo(() => (templates.data ?? []).filter((template) => template.status === 'ACTIVE' && template.version.status === 'PUBLISHED'), [templates.data]);
  const requestItems = useMemo(() => requests.data?.data ?? [], [requests.data]);
  const categories = useMemo(() => [
    { value: '', label: '전체' },
    ...Array.from(new Set(availableTemplates.map(workflowTemplateCategory)))
      .sort((left, right) => left.localeCompare(right, 'ko'))
      .map((value) => ({ value, label: value })),
  ], [availableTemplates]);
  const filteredTemplates = useMemo(() => {
    const normalizedQuery = templateQuery.trim().toLocaleLowerCase('ko');
    return availableTemplates.filter((template) => {
      const categoryMatches = !category || workflowTemplateCategory(template) === category;
      const queryMatches = !normalizedQuery || `${template.displayName} ${template.code} ${template.category ?? ''} ${template.version.description ?? ''}`.toLocaleLowerCase('ko').includes(normalizedQuery);
      return categoryMatches && queryMatches;
    });
  }, [availableTemplates, category, templateQuery]);
  const filteredRequests = useMemo(() => {
    const normalizedQuery = requestQuery.trim().toLocaleLowerCase('ko');
    return requestItems.filter((request) => {
      const statusMatches = requestStatus === 'ALL'
        || (requestStatus === 'CLOSED' ? request.status === 'DENIED' || request.status === 'CANCELLED' : workflowRequestDisplayStatus(request) === requestStatus);
      const queryMatches = !normalizedQuery || `${request.title} ${request.templateName}`.toLocaleLowerCase('ko').includes(normalizedQuery);
      const date = formatWorkflowDateKey(request.submittedAt || request.createdAt);
      const templateMatches = !requestFilters.templateId || request.templateId === requestFilters.templateId;
      const dateMatches = (!requestFilters.dateFrom || date >= requestFilters.dateFrom) && (!requestFilters.dateTo || date <= requestFilters.dateTo);
      return statusMatches && queryMatches && templateMatches && dateMatches;
    });
  }, [requestItems, requestQuery, requestStatus, requestFilters]);
  useEffect(() => {
    if (category && !categories.some((item) => item.value === category)) setCategory('');
  }, [categories, category]);
  useEffect(() => {
    const syncRouteFromLocation = () => {
      const currentPage = route.currentPage();
      const draftID = currentPage === 'my-workflows' ? workflowComposerDraftIDFromLocation() : undefined;
      if (currentPage === 'my-workflows') setComposerPresentation(workflowComposerPresentationFromLocation());
      setRouteDraftID(draftID);
      setRouteRequestID(currentPage === 'my-workflow-requests' ? workflowRequestIDFromLocation() : undefined);
      if (currentPage === 'my-workflow-requests') {
        setRequestStatus(workflowRequestStatusFromLocation());
        setSelectedPresentation(new URLSearchParams(window.location.search).get('presentation') === 'fullscreen' ? 'standalone' : 'dialog');
        const search = new URLSearchParams(window.location.search);
        setRequestQuery(search.get('search') ?? '');
        setRequestFilters({ templateId: search.get('templateId') ?? '', dateFrom: search.get('dateFrom') ?? '', dateTo: search.get('dateTo') ?? '' });
      }
    };
    syncRouteFromLocation();
    return route.subscribe(syncRouteFromLocation);
  }, [route]);
  useEffect(() => {
    if (!routeRequestID) {
      detailRequestSequence.current += 1;
      setSelectedRequest(undefined);
      setRequestDetailStatus('idle');
      setActionError('');
      return undefined;
    }
    const sequence = ++detailRequestSequence.current;
    detailSubmitIntentRef.current = undefined;
    setDetailSubmitRetryPending(false);
    setSelectedRequest((current) => current?.id === routeRequestID ? current : workflowRequestLoadingPlaceholder(routeRequestID));
    setActionError('');
    if (!canReadRequests) {
      setRequestDetailStatus('error');
      setActionError('이 요청을 볼 권한이 없습니다.');
      return undefined;
    }
    setRequestDetailStatus(fixtureMode ? 'ready' : 'loading');
    const detailRequest = fixtureMode
      ? Promise.resolve(fixtureRequestItems.find((request) => request.id === routeRequestID))
      : port.getWorkflowRequest(routeRequestID);
    void detailRequest.then((detail) => {
      if (detailRequestSequence.current !== sequence) return;
      if (!detail || detail.id !== routeRequestID) throw new Error('요청 상세를 찾을 수 없습니다.');
      setSelectedRequest(detail);
      setRequestDetailStatus('ready');
    }).catch((reason: unknown) => {
      if (detailRequestSequence.current !== sequence) return;
      setActionError(reason instanceof Error ? reason.message : '요청 상세를 불러오지 못했습니다.');
      setRequestDetailStatus('error');
    });
    return () => {
      if (detailRequestSequence.current === sequence) detailRequestSequence.current += 1;
    };
  }, [canReadRequests, port, fixtureMode, fixtureRequestItems, routeRequestID, detailRetry]);
  useEffect(() => {
    if (!routeDraftID || (createOpen && activeComposerDraftID.current === routeDraftID)) return undefined;
    if (!canCreate || !canReadTemplates) {
      const nextView = canReadRequests ? 'requests' : 'create';
      setRouteDraftID(undefined);
      setDraftRestoreStatus('idle');
      setRequestWarning('이 초안을 작성할 권한이 없습니다. 내 요청에서 현재 상태를 확인해 주세요.');
      route.navigate(nextView === 'requests' ? 'my-workflow-requests' : 'my-workflows', undefined, { replace: true });
      return undefined;
    }
    const sequence = ++draftRestoreSequence.current;
    setDraftRestoreStatus('loading');
    setDraftRestoreError('');
    const restoredRequest = fixtureMode
      ? Promise.resolve(fixtureRequestItems.find((request) => request.id === routeDraftID))
      : port.getWorkflowRequest(routeDraftID);
    void restoredRequest.then((request) => {
      if (draftRestoreSequence.current !== sequence) return;
      if (!request || !isEditableWorkflowDraft(request)) {
        throw new Error('이 초안은 더 이상 작성할 수 없습니다. 내 요청에서 현재 상태를 확인해 주세요.');
      }
      activeComposerDraftID.current = request.id;
      setEditingRequest(request);
      setCreateTemplateId(request.templateId);
      setCreateOpen(true);
      setDraftRestoreStatus('idle');
    }).catch((reason: unknown) => {
      if (draftRestoreSequence.current !== sequence) return;
      setDraftRestoreStatus('error');
      setDraftRestoreError(reason instanceof Error ? reason.message : '저장한 초안을 불러오지 못했습니다.');
    });
    return () => { draftRestoreSequence.current += 1; };
  }, [canCreate, canReadRequests, canReadTemplates, port, createOpen, draftRestoreRetry, fixtureMode, fixtureRequestItems, routeDraftID]);
  useEffect(() => {
    const targetView = requestCompletionTarget.current;
    if (createOpen || !requestNotice || !targetView) return;
    const frame = window.requestAnimationFrame(() => {
      const panelId = targetView === 'requests' ? 'my-workflow-requests' : 'workflow-request-create-panel';
      const focusTargetId = targetView === 'requests' ? 'my-workflow-requests' : 'workflow-template-catalog-start';
      document.getElementById(panelId)?.scrollIntoView({ block: 'start' });
      document.getElementById(focusTargetId)?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [createOpen, requestCompletionSequence, requestNotice]);
  const openCreate = (templateId: string) => {
    if (!canCreate) return;
    composerReturnContext.current = undefined;
    activeComposerDraftID.current = undefined;
    setComposerPresentation('dialog');
    setEditingRequest(undefined); setCreateTemplateId(templateId); setCreateOpen(true); setActionError(''); setRequestNotice(''); setSubmittedResult(undefined); setRequestWarning('');
  };
  const startDraft = (request: WorkflowRequest) => {
    if (route.currentPage() === 'my-workflow-requests') {
      composerReturnContext.current = { status: requestStatus, search: requestQuery, filters: { ...requestFilters } };
    }
    activeComposerDraftID.current = request.id;
    setComposerPresentation('dialog');
    setEditingRequest(request); setCreateTemplateId(request.templateId); setCreateOpen(true); closeRequest(false);
    const path = workflowComposerPath(route, request.id, 'dialog');
    if (`${window.location.pathname}${window.location.search}` !== path) navigateDetail(() => route.navigate('my-workflows', new URLSearchParams({ draft: request.id })));
  };
  const openDraft = (request: WorkflowRequest) => {
    if (detailActionPending.current || detailSubmitRetryPending || !canCreate || !canReadTemplates || !isEditableWorkflowDraft(request) || !allowDetailLeave()) return;
    startDraft(request);
  };
  const openRequest = (request: WorkflowRequest, resume = false) => {
    if (detailActionPending.current || !allowDetailLeave()) return;
    setResumeDraftID(resume ? request.id : undefined);
    detailSubmitIntentRef.current = undefined;
    setDetailSubmitRetryPending(false);
    setSelectedRequest(request); setActionError(''); setRequestDetailStatus(fixtureMode ? 'ready' : 'loading');
    setRouteRequestID(request.id);
    navigateDetail(() => route.navigate('my-workflow-requests', workflowRequestRouteSearch(requestStatus, request.id, requestQuery, requestFilters)));
  };
  const detailBusy = detailSubmitSaving || detailCancelSaving || selectedCommentBusy || copySaving || personnelBusy;
  const personnelTarget = workflowPersonnelRequestTarget(selectedRequest);
  const cancellingCompletedTimeOff = selectedRequest?.target?.resourceType === 'TIME_OFF_REQUEST' && selectedRequest.status === 'COMPLETED';
  const allowDetailAction = () => !detailBusy && (!selectedCommentDirty || window.confirm('작성 중인 댓글을 버리고 이동할까요?'));
  const allowDetailLeave = () => !detailSubmitRetryPending && allowDetailAction();
  useEffect(() => {
    if (!selectedRequest) return;
    return registerNavigationGuard(() => skipDetailGuard.current || allowDetailLeave());
  // 현재 상세의 입력과 처리 상태만 guard가 소유합니다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequest?.id, detailBusy, detailSubmitRetryPending, selectedCommentDirty]);
  useEffect(() => {
    if (!selectedRequest || (!detailBusy && !detailSubmitRetryPending)) return;
    const preventAccidentalUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventAccidentalUnload);
    return () => window.removeEventListener('beforeunload', preventAccidentalUnload);
  }, [selectedRequest?.id, detailBusy, detailSubmitRetryPending]);
  const navigateDetail = (action: () => void) => {
    skipDetailGuard.current = true;
    try { action(); } finally { skipDetailGuard.current = false; }
  };
  const closeRequest = (syncRoute = true) => {
    if (syncRoute && !allowDetailLeave()) return;
    setSelectedCommentDirty(false); setSelectedCommentBusy(false); setSelectedPresentation('dialog'); setCancelOpen(false);
    setResumeDraftID(undefined);
    detailRequestSequence.current += 1;
    detailSubmitIntentRef.current = undefined;
    setDetailSubmitRetryPending(false);
    setRouteRequestID(undefined);
    setSelectedRequest(undefined); setActionError(''); setRequestDetailStatus('idle');
    if (syncRoute && route.currentPage() === 'my-workflow-requests') {
      navigateDetail(() => route.navigate('my-workflow-requests', workflowRequestRouteSearch(requestStatus, undefined, requestQuery, requestFilters)));
    }
  };
  const changeActiveView = (nextView: WorkflowRequestCenterView) => {
    route.navigate(nextView === 'requests' ? 'my-workflow-requests' : 'my-workflows');
  };
  const changeRequestStatus = (status: RequestStatusFilter) => {
    setRequestStatus(status);
    route.navigate('my-workflow-requests', workflowRequestRouteSearch(status, undefined, requestQuery, requestFilters), { replace: true });
  };
  const changeRequestFilters = (patch: Partial<typeof requestFilters>) => {
    const next = { ...requestFilters, ...patch }; setRequestFilters(next);
    route.navigate('my-workflow-requests', workflowRequestRouteSearch(requestStatus, undefined, requestQuery, next), { replace: true });
  };
  const changeRequestQuery = (search: string) => {
    setRequestQuery(search);
    route.navigate('my-workflow-requests', workflowRequestRouteSearch(requestStatus, undefined, search, requestFilters), { replace: true });
  };
  useEffect(() => {
    if (!resumeDraftID || selectedRequest?.id !== resumeDraftID || requestDetailStatus !== 'ready') return;
    setResumeDraftID(undefined);
    if (canCreate && canReadTemplates && isEditableWorkflowDraft(selectedRequest)) openDraft(selectedRequest);
    else setActionError(selectedRequest.target ? '이 요청은 연결된 업무 화면에서 작성할 수 있습니다.' : '현재 요청 상태 또는 권한으로는 이어서 작성할 수 없습니다.');
  // 상세 조회 뒤 현재 상태와 권한을 확인한 초안만 작성기로 전환합니다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeDraftID, selectedRequest, requestDetailStatus, canCreate, canReadTemplates]);
  const finishRequestAction = (notice: string, showRequests = canReadRequests, result?: { requestId?: string }) => {
    const nextView = showRequests ? 'requests' : 'create';
    const context = composerReturnContext.current;
    composerReturnContext.current = undefined;
    requests.reload(); onRequestsChanged?.();
    setRequestQuery(context?.search ?? ''); setRequestStatus(context?.status ?? 'ALL'); setRequestFilters(context?.filters ?? { templateId: '', dateFrom: '', dateTo: '' }); setRequestNotice(notice);
    setSubmittedResult(result);
    requestCompletionTarget.current = nextView;
    route.navigate(nextView === 'requests' ? 'my-workflow-requests' : 'my-workflows', nextView === 'requests' && context ? workflowRequestRouteSearch(context.status, undefined, context.search, context.filters) : undefined);
    setRequestCompletionSequence((current) => current + 1);
  };
  const closeComposer = (savedDraftID?: string) => {
    draftRestoreSequence.current += 1;
    activeComposerDraftID.current = undefined;
    setRouteDraftID(undefined);
    setComposerPresentation('dialog');
    setCreateOpen(false); setEditingRequest(undefined); setCreateTemplateId(undefined);
    if (savedDraftID) {
      finishRequestAction('마지막으로 저장된 초안을 내 요청에서 다시 열 수 있습니다.');
      return;
    }
    const context = composerReturnContext.current;
    composerReturnContext.current = undefined;
    if (context) route.navigate('my-workflow-requests', workflowRequestRouteSearch(context.status, undefined, context.search, context.filters));
    else changeActiveView('create');
  };
  const rememberComposerDraft = (draftID: string) => {
    activeComposerDraftID.current = draftID;
    const path = workflowComposerPath(route, draftID, composerPresentation);
    if (`${window.location.pathname}${window.location.search}` !== path) {
      const search = new URLSearchParams({ draft: draftID });
      if (composerPresentation === 'standalone') search.set('composer', 'fullscreen');
      route.navigate('my-workflows', search);
    }
  };
  const changeComposerPresentation = (nextPresentation: WorkflowComposerPresentation) => {
    setComposerPresentation(nextPresentation);
    const draftID = activeComposerDraftID.current ?? routeDraftID;
    const path = workflowComposerPath(route, draftID, nextPresentation);
    if (`${window.location.pathname}${window.location.search}` === path) return;
    const search = new URLSearchParams();
    if (draftID) search.set('draft', draftID);
    if (nextPresentation === 'standalone') search.set('composer', 'fullscreen');
    route.navigate('my-workflows', search.size ? search : undefined);
  };
  useEffect(() => {
    if (routeDraftID || !createOpen || !activeComposerDraftID.current) return;
    const savedDraftID = activeComposerDraftID.current;
    closeComposer(savedDraftID);
  // 브라우저 뒤로가기로 draft query가 사라진 경우 저장된 초안을 목록에서 바로 다시 불러옵니다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDraftID]);
  const cancelRequest = async () => {
    if (!selectedRequest || requestDetailStatus !== 'ready' || detailActionPending.current || detailSubmitRetryPending || !allowDetailLeave()) return;
    const timeOffTarget = selectedRequest.target?.resourceType === 'TIME_OFF_REQUEST'
      ? selectedRequest.target
      : undefined;
    if ((!timeOffTarget && !canCancel) || (timeOffTarget && !canCancelTimeOff)) return;
    const requestId = selectedRequest.id;
    const comment = cancelComment.trim();
    if (selectedRequest.status !== 'DRAFT' && !comment) return;
    if (!cancelIntent.current || cancelIntent.current.requestId !== requestId || cancelIntent.current.comment !== comment) cancelIntent.current = { requestId, comment, key: crypto.randomUUID() };
    const intent = cancelIntent.current;
    const sequence = detailRequestSequence.current;
    detailActionPending.current = true;
    setDetailCancelSaving(true);
    setActionError('');
    try {
      if (!fixtureMode) {
        if (timeOffTarget) await port.cancelTimeOffRequest(timeOffTarget.resourceId, comment, intent.key);
        else await port.cancelWorkflowRequest(requestId, comment, intent.key);
      }
      if (!isCurrentDetail(requestId, sequence)) return;
      cancelIntent.current = undefined;
      closeRequest(false);
      navigateDetail(() => finishRequestAction(timeOffTarget && selectedRequest.status === 'COMPLETED'
        ? '휴가 취소 요청을 보냈습니다.'
        : '요청을 취소했습니다.'));
    } catch (reason) {
      if (!isCurrentDetail(requestId, sequence)) return;
      const message = reason instanceof Error ? reason.message : '요청을 취소하지 못했습니다.';
      if (timeOffTarget) {
        try {
          const latest = await port.getTimeOffRequest(timeOffTarget.resourceId);
          if (!isCurrentDetail(requestId, sequence)) return;
          if (latest.status === 'CANCELLED' || latest.cancellationStatus === 'SUBMITTED') {
            cancelIntent.current = undefined;
            closeRequest(false);
            navigateDetail(() => finishRequestAction(latest.cancellationStatus === 'SUBMITTED' ? '휴가 취소 승인을 요청했습니다.' : '휴가 신청을 취소했습니다.'));
            return;
          }
        } catch { /* 응답이 불확실하면 같은 취소 키로 재시도합니다. */ }
        if (!isCurrentDetail(requestId, sequence)) return;
        if (!isAmbiguousApiMutationFailure(reason)) cancelIntent.current = undefined;
        setActionError(message);
        return;
      }
      try {
        const latest = fixtureMode ? selectedRequest : await port.getWorkflowRequest(requestId);
        if (!isCurrentDetail(requestId, sequence)) return;
        if (latest.status === 'CANCELLED') {
          cancelIntent.current = undefined; closeRequest(false);
          navigateDetail(() => finishRequestAction('요청이 취소된 것을 확인했습니다.'));
        } else {
          setSelectedRequest(latest); requests.reload();
          setActionError(`${message} 현재 상태는 ${statusLabel[workflowRequestDisplayStatus(latest)] ?? latest.status}입니다. 내용을 확인한 후 다시 시도해 주세요.`);
        }
      } catch {
        setActionError(`${message} 최신 상태도 확인하지 못했습니다. 같은 취소를 다시 시도할 수 있습니다.`);
      }
    } finally {
      detailActionPending.current = false;
      setDetailCancelSaving(false);
    }
  };
  const submitRequest = async () => {
    if (!canCreate || !selectedRequest || selectedRequest.draftVersion === undefined || requestDetailStatus !== 'ready' || detailActionPending.current || !allowDetailAction()) return;
    const draftId = selectedRequest.id;
    const draftVersion = selectedRequest.draftVersion;
    const detailSequence = detailRequestSequence.current;
    const currentIntent = detailSubmitIntentRef.current;
    const intent = currentIntent?.draftId === draftId && currentIntent.draftVersion === draftVersion
      ? currentIntent
      : { draftId, draftVersion, idempotencyKey: crypto.randomUUID() };
    detailSubmitIntentRef.current = intent;
    detailActionPending.current = true;
    setDetailSubmitSaving(true);
    setActionError('');
    try {
      const submitted = fixtureMode
        ? submitFixtureWorkflowRequest(selectedRequest, requesterWorkerId)
        : await port.submitWorkflowRequest(intent.draftId, intent.draftVersion, intent.idempotencyKey);
      if (!isCurrentDetail(draftId, detailSequence)) return;
      if (fixtureMode) rememberFixtureRequest(submitted);
      detailSubmitIntentRef.current = undefined;
      setDetailSubmitRetryPending(false);
      closeRequest(false); navigateDetail(() => finishRequestAction('요청을 제출했습니다. 내 요청에서 진행 상태를 확인할 수 있습니다.', canReadRequests, { requestId: submitted.id }));
    } catch (reason) {
      if (!isCurrentDetail(draftId, detailSequence)) return;
      const draftConflict = workflowErrorCode(reason) === 'WORKFLOW_DRAFT_VERSION_CONFLICT';
      const ambiguous = isAmbiguousApiMutationFailure(reason);
      if (!ambiguous || draftConflict) detailSubmitIntentRef.current = undefined;
      if (draftConflict) {
        setDetailSubmitRetryPending(false);
        try {
          const latest = await port.getWorkflowRequest(draftId);
          if (!isCurrentDetail(draftId, detailSequence)) return;
          if (latest.id !== draftId) throw new Error('현재 초안과 상세 정보가 일치하지 않습니다.');
          setSelectedRequest(latest);
          setActionError(['DRAFT', 'NEEDS_CHANGES'].includes(latest.status)
            ? '다른 창에서 변경된 최신 초안을 불러왔습니다. 내용을 확인한 후 다시 제출해 주세요.'
            : '다른 창에서 변경된 최신 요청 상태를 불러왔습니다.');
          requests.reload();
        } catch (reloadReason) {
          if (!isCurrentDetail(draftId, detailSequence)) return;
          setActionError(reloadReason instanceof Error
            ? `다른 창에서 초안이 변경되었지만 최신 내용을 불러오지 못했습니다. ${reloadReason.message}`
            : '다른 창에서 초안이 변경되었지만 최신 내용을 불러오지 못했습니다.');
        }
        return;
      }
      setDetailSubmitRetryPending(ambiguous);
      setActionError(ambiguous
        ? '제출 결과를 확인하지 못해 편집과 이동을 잠시 잠갔습니다. 같은 제출을 다시 시도하거나 최신 상태를 확인해 주세요.'
        : reason instanceof Error ? reason.message : '제출하지 못했습니다.');
    } finally {
      detailActionPending.current = false;
      setDetailSubmitSaving(false);
    }
  };
  const checkDetailSubmission = async () => {
    const intent = detailSubmitIntentRef.current;
    if (!intent || !detailSubmitRetryPending || detailActionPending.current || detailBusy) return;
    const sequence = detailRequestSequence.current;
    detailActionPending.current = true;
    setDetailSubmitSaving(true);
    try {
      const latest = await port.getWorkflowRequest(intent.draftId);
      if (!isCurrentDetail(intent.draftId, sequence)) return;
      if (latest.id !== intent.draftId) throw new Error('현재 요청과 조회한 요청이 일치하지 않습니다.');
      if (['DRAFT', 'NEEDS_CHANGES'].includes(latest.status) && latest.draftVersion === intent.draftVersion) {
        setActionError('아직 제출 결과가 확정되지 않았습니다. 같은 제출을 안전하게 다시 시도할 수 있습니다.');
        return;
      }
      detailSubmitIntentRef.current = undefined;
      setDetailSubmitRetryPending(false);
      setSelectedRequest(latest);
      setActionError('');
      requests.reload(); onRequestsChanged?.();
    } catch {
      if (isCurrentDetail(intent.draftId, sequence)) setActionError('최신 상태를 확인하지 못했습니다. 다시 확인하거나 같은 제출을 재시도해 주세요.');
    } finally {
      detailActionPending.current = false;
      setDetailSubmitSaving(false);
    }
  };
  const copyRequest = async () => {
    if (!selectedRequest || selectedRequest.target || selectedRequest.status !== 'DENIED' || detailActionPending.current || !canCreate || !canReadTemplates || !allowDetailLeave()) return;
    const source = selectedRequest;
    if (!copyIntent.current || copyIntent.current.requestId !== source.id) copyIntent.current = { requestId: source.id, key: crypto.randomUUID() };
    detailActionPending.current = true;
    setCopySaving(true); setActionError('');
    try {
      const copied: WorkflowRequest = fixtureMode ? { ...structuredClone(source), id: `fixture-copy-${crypto.randomUUID()}`, status: 'DRAFT', draftVersion: 1, copiedFromRequestId: source.id, values: Object.fromEntries(Object.entries(source.values ?? {}).filter(([key]) => !source.fields?.some((field) => field.key === key && field.type === 'FILE'))) } : await port.copyWorkflowRequest(source.id, copyIntent.current.key);
      if (fixtureMode) rememberFixtureRequest(copied);
      copyIntent.current = undefined;
      setRequestWarning('반려된 요청을 복사했습니다. 첨부 파일은 다시 등록해 주세요.');
      startDraft(copied);
    } catch (reason) { setActionError(reason instanceof Error ? reason.message : '요청을 복사하지 못했습니다. 다시 시도해 주세요.'); }
    finally { detailActionPending.current = false; setCopySaving(false); }
  };
  const hasRequestFilters = Boolean(requestQuery || requestStatus !== 'ALL' || requestFilters.templateId || requestFilters.dateFrom || requestFilters.dateTo);
  const submissionFeedback = submittedResult && requestNotice ? <Box sx={{ mb: 2 }}>
    <ResultState
      tone="success"
      title="요청 제출 완료"
      description={requestNotice}
      action={<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
        {canReadRequests && submittedResult.requestId ? <Button variant="weak" onClick={() => {
          const requestId = submittedResult.requestId;
          if (!requestId) return;
          setRequestStatus('ALL');
          setRouteRequestID(requestId);
          route.navigate('my-workflow-requests', workflowRequestRouteSearch('ALL', requestId));
          setSubmittedResult(undefined); setRequestNotice('');
        }}>요청 보기</Button> : null}
        <Button color="inherit" onClick={() => { setSubmittedResult(undefined); setRequestNotice(''); }}>닫기</Button>
      </Stack>}
    />
  </Box> : null;
  const fillEmptySpace = !submissionFeedback && (activeView === 'create'
    ? canReadTemplates && filteredTemplates.length === 0
    : canReadRequests && filteredRequests.length === 0);
  const emptyPageLayout = fillEmptySpace
    ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' } as const
    : undefined;
  if (routeDraftID && draftRestoreStatus !== 'idle' && !createOpen) {
    return <GuidePage title="워크플로우">
      <ResourceState
        loading={draftRestoreStatus === 'loading'}
        error={draftRestoreStatus === 'error' ? draftRestoreError : undefined}
        empty={false}
        errorAction={<Stack direction="row" spacing={1} sx={{ mt: 0.75 }}><Button variant="outlined" onClick={() => setDraftRestoreRetry((current) => current + 1)}>다시 시도</Button><Button onClick={() => closeComposer(routeDraftID)}>내 요청으로</Button></Stack>}
        loadingLabel="저장한 초안을 불러오는 중입니다."
      >
        {null}
      </ResourceState>
    </GuidePage>;
  }
  return <>
    <Box
      aria-hidden={(createOpen && composerPresentation === 'standalone') || (Boolean(selectedRequest) && selectedPresentation === 'standalone')}
      inert={(createOpen && composerPresentation === 'standalone') || (Boolean(selectedRequest) && selectedPresentation === 'standalone') ? true : undefined}
      sx={{ display: (createOpen && composerPresentation === 'standalone') || (Boolean(selectedRequest) && selectedPresentation === 'standalone') ? 'none' : 'block', height: fillEmptySpace ? '100%' : undefined }}
    >
    <GuidePage
      title={activeView === 'create' ? '요청하기' : '내 요청'}
      width="standard"
      fill={fillEmptySpace}
      refreshAction={activeView === 'create'
        ? canReadTemplates ? { onClick: templates.reload, loading: templates.loading } : undefined
        : canReadRequests ? { onClick: requests.reload, loading: requests.loading } : undefined}
      primaryAction={canCreate && canReadTemplates && activeView === 'requests' ? <Button variant="fill" startIcon={<AddOutlined />} onClick={() => changeActiveView('create')} disabled={templates.loading || Boolean(templates.error) || availableTemplates.length === 0} title={templates.error ? '신청 가능한 양식을 불러오지 못했습니다.' : availableTemplates.length === 0 ? '현재 신청 가능한 양식이 없습니다.' : undefined}>양식에서 시작</Button> : undefined}
    >
      <Box sx={emptyPageLayout}>
        <FeedbackSnackbar open={Boolean(requestNotice) && !submittedResult} message={requestNotice} tone="success" onClose={() => setRequestNotice('')} />
        {requestWarning && <Alert severity="warning" onClose={() => setRequestWarning('')} sx={{ mb: 1.5 }}>{requestWarning}</Alert>}
        {activeView === 'create' && <Box id="workflow-request-create-panel" component="section" aria-label="요청하기" sx={emptyPageLayout}>
          {submissionFeedback}
          {canReadTemplates ? <>
            <Box
              id="workflow-template-catalog-start"
              data-testid="workflow-template-catalog-start"
              tabIndex={-1}
              aria-label="신청 가능한 양식"
              sx={{ display: 'flex', flexDirection: 'column', gap: `${designTokens.layout.pageSectionGap}px`, borderRadius: designTokens.radius.surface, '&:focus-visible': createFocusRingStyles() }}
            >
              <Box data-testid="workflow-template-categories">
                <FilterTabs
                  value={category}
                  items={categories.map((item) => ({ ...item, controls: 'workflow-template-grid' }))}
                  ariaLabel="워크플로우 양식 분류"
                  onChange={setCategory}
                />
              </Box>
              <CommonFilterBar
                query={templateQuery}
                onQueryChange={setTemplateQuery}
                searchPlaceholder="양식 이름, 분류 또는 설명"
                searchLabel="워크플로우 검색"
              />
            </Box>
            <Box sx={{ mt: `${designTokens.layout.pageSectionGap}px`, ...(fillEmptySpace ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { minHeight: 220 }) }}>
              <ResourceState
                embedded
                fill={fillEmptySpace}
                loading={templates.loading}
                error={templates.error?.message}
                onRetry={templates.reload}
                empty={!templates.loading && !templates.error && filteredTemplates.length === 0}
                emptyTitle={templateQuery || category ? '조건에 맞는 양식이 없습니다.' : '아직 신청 가능한 양식이 없습니다.'}
                emptyDescription={templateQuery || category ? '검색어 또는 분류를 바꿔 다시 찾아보세요.' : '관리자가 양식을 게시하면 이곳에서 바로 요청을 시작할 수 있습니다.'}
                emptyAction={templateQuery || category ? <Button variant="weak" onClick={() => { setTemplateQuery(''); setCategory(''); }}>검색 조건 초기화</Button> : false}
              >
                <Box
                  id="workflow-template-grid"
                  component="ul"
                  data-testid="workflow-template-grid"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                      lg: 'repeat(5, minmax(0, 1fr))',
                    },
                    gap: { xs: 1, sm: 1.25 },
                    p: 0,
                    m: 0,
                    listStyle: 'none',
                  }}
                >
                  {filteredTemplates.map((template) => <Box component="li" key={template.id}><WorkflowTemplateCard template={template} disabled={!canCreate} onClick={() => openCreate(template.id)} /></Box>)}
                </Box>
              </ResourceState>
            </Box>
          </> : <Alert severity="info">신청 가능한 양식을 볼 권한이 없습니다.</Alert>}
        </Box>}

        {activeView === 'requests' && <Box id="my-workflow-requests" data-testid="workflow-request-list" component="section" aria-label="내 요청 목록" tabIndex={-1} sx={{ ...emptyPageLayout, scrollMarginTop: 16, borderRadius: designTokens.radius.surface, '&:focus-visible': createFocusRingStyles() }}>
          {submissionFeedback}
          {canReadRequests ? <>
            <CommonFilterBar
              query={requestQuery}
              onQueryChange={changeRequestQuery}
              searchPlaceholder="요청 제목 또는 양식 이름"
              searchLabel="내 요청 검색"
              filters={[
                { label: '상태', value: requestStatus === 'ALL' ? '' : requestStatus, onChange: (value) => changeRequestStatus((value || 'ALL') as RequestStatusFilter), options: requestStatusFilters.map((filter) => ({ label: filter.label, value: filter.value === 'ALL' ? '' : filter.value })) },
                { label: '양식', value: requestFilters.templateId, onChange: (templateId) => changeRequestFilters({ templateId }), options: [{ label: '전체', value: '' }, ...Array.from(new Map(requestItems.map((request) => [request.templateId, { value: request.templateId, label: request.templateName }])).values())] },
                { label: '제출 시작일', kind: 'date', value: requestFilters.dateFrom, onChange: (dateFrom) => changeRequestFilters({ dateFrom }) },
                { label: '제출 종료일', kind: 'date', value: requestFilters.dateTo, onChange: (dateTo) => changeRequestFilters({ dateTo }) },
              ]}
            />
            {requestFilters.dateFrom && requestFilters.dateTo && requestFilters.dateFrom > requestFilters.dateTo ? <Alert severity="warning" sx={{ mt: 1 }}>제출 종료일은 시작일과 같거나 이후여야 합니다.</Alert> : null}
            <Box sx={{ mt: `${designTokens.layout.pageSectionGap}px`, ...(fillEmptySpace ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { minHeight: 174 }) }}>
              <ResourceState
                embedded
                fill={fillEmptySpace}
                loading={requests.loading}
                error={requests.error?.message}
                onRetry={requests.reload}
                empty={!requests.loading && !requests.error && filteredRequests.length === 0}
                emptyTitle={hasRequestFilters ? '조건에 맞는 요청이 없습니다.' : '아직 시작한 요청이 없습니다.'}
                emptyDescription={hasRequestFilters ? '검색어나 상태 조건을 바꿔 다시 확인해 보세요.' : canCreate && canReadTemplates ? '신청할 양식을 선택해 첫 요청을 시작해 보세요.' : '작성하거나 제출한 요청이 이곳에 표시됩니다.'}
                emptyAction={hasRequestFilters
                  ? <Button variant="weak" onClick={() => { setRequestQuery(''); setRequestStatus('ALL'); setRequestFilters({ templateId: '', dateFrom: '', dateTo: '' }); route.navigate('my-workflow-requests', undefined, { replace: true }); }}>검색 조건 초기화</Button>
                  : canCreate && canReadTemplates ? <Button variant="fill" onClick={() => changeActiveView('create')}>신청 양식 보기</Button> : false}
              >
                <Paper variant="outlined" sx={{ borderRadius: designTokens.radius.surface }}>
                  <Stack divider={<Divider />} sx={{
                    borderRadius: 'inherit',
                    // 첫 행·마지막 행의 hover와 초점도 목록 표면의 모서리를 따릅니다.
                    '& > .MuiBox-root:first-of-type > [data-slot="request-detail"]': { borderStartStartRadius: 'inherit', borderStartEndRadius: 'inherit' },
                    '& > .MuiBox-root:last-of-type > [data-slot="request-detail"]': { borderEndStartRadius: 'inherit', borderEndEndRadius: 'inherit' },
                  }}>{filteredRequests.map((request) => <WorkflowRequestListItem key={request.id} request={request} onClick={() => openRequest(request)} onResume={canCreate && canReadTemplates && ['DRAFT', 'NEEDS_CHANGES'].includes(request.status) && !request.target ? () => openRequest(request, true) : undefined} />)}</Stack>
                </Paper>
              </ResourceState>
            </Box>
          </> : <Alert severity="info" sx={{ mt: 1.5 }}>내 요청 이력을 볼 권한이 없습니다.</Alert>}
        </Box>}
      </Box>
    </GuidePage>
    </Box>
    {selectedRequest ? <WorkflowRequestDocumentWorkspace
      port={port}
      approvalSettingsPort={approvalSettingsPort}
      directoryPort={directoryPort}
      request={selectedRequest}
      presentation={selectedPresentation}
      onPresentationChange={(presentation) => {
        if (!allowDetailLeave()) return;
        const search = workflowRequestRouteSearch(requestStatus, selectedRequest.id, requestQuery, requestFilters) ?? new URLSearchParams();
        if (presentation === 'standalone') search.set('presentation', 'fullscreen');
        navigateDetail(() => route.navigate('my-workflow-requests', search, { replace: true }));
      }}
      onCommentDraftChange={setSelectedCommentDirty}
      canManageContracts={!readOnly && (fixtureMode || scopes.includes('rewards.write'))}
      onCommentBusyChange={setSelectedCommentBusy}
      onOpenReference={(id) => { if (allowDetailLeave()) navigateDetail(() => route.navigate('my-workflow-requests', workflowRequestRouteSearch(requestStatus, id, requestQuery, requestFilters))); }}
      onCopy={!selectedRequest.target && selectedRequest.status === 'DENIED' && canCreate ? () => void copyRequest() : undefined}
      fixtureMode={fixtureMode}
      loading={requestDetailStatus === 'loading'}
      error={requestDetailStatus === 'error' ? actionError : undefined}
      onRetry={requestDetailStatus === 'error' ? () => setDetailRetry((value) => value + 1) : undefined}
      onRefreshRequest={async () => {
        const id = selectedRequest.id; const sequence = detailRequestSequence.current;
        const latest = await port.getWorkflowRequest(id);
        if (!isCurrentDetail(id, sequence)) return;
        if (latest.id !== id) throw new Error('요청 상태를 확인하지 못했습니다. 다시 확인해 주세요.');
        setSelectedRequest(latest); requests.reload();
      }}
      actionError={requestDetailStatus === 'ready' ? actionError : undefined}
      canComment={requestDetailStatus === 'ready' && !detailSubmitSaving && !detailCancelSaving && !detailSubmitRetryPending && !copySaving && !readOnly && scopes.includes('workflow.request.comment')}
      onClose={closeRequest}
      onEdit={requestDetailStatus === 'ready' && !selectedRequest.target && canCreate && canReadTemplates && isEditableWorkflowDraft(selectedRequest) ? () => openDraft(selectedRequest) : undefined}
      editActionInFooter={selectedRequest.status === 'NEEDS_CHANGES' && canCreate && canReadTemplates && !selectedRequest.target}
      editDisabled={detailBusy || detailSubmitRetryPending}
      busy={detailBusy || detailSubmitRetryPending}
      footer={staffingPort && personnelTarget && canManagePersonnel && requestDetailStatus === 'ready' ? <WorkerPersonnelRequestActions key={personnelTarget.id} target={personnelTarget} port={staffingPort} fixtureMode={fixtureMode} canEdit={canManagePersonnel} canCancel={canCancelPersonnel} disabled={detailSubmitSaving || detailCancelSaving || selectedCommentBusy || copySaving || detailSubmitRetryPending} onBusyChange={setPersonnelBusy} onChanged={() => { requests.reload(); setDetailRetry(value => value + 1); onRequestsChanged?.(); }} onOpenWorkflow={reference => { if (allowDetailLeave()) navigateDetail(() => route.navigate('my-workflow-requests', workflowRequestRouteSearch(requestStatus, reference.id, requestQuery, requestFilters))); }} /> : requestDetailStatus === 'ready' && (
        selectedRequest.target?.resourceType === 'TIME_OFF_REQUEST'
          ? canCancelTimeOff && ['DRAFT', 'IN_PROGRESS', 'COMPLETED'].includes(selectedRequest.status)
          : !selectedRequest.target && ((['DRAFT', 'NEEDS_CHANGES'].includes(selectedRequest.status) && (canCreate || canCancel)) || (selectedRequest.status === 'IN_PROGRESS' && canCancel))
      ) ? <>
        {detailSubmitRetryPending ? <Button variant="outlined" disabled={detailBusy} onClick={() => void checkDetailSubmission()}>최신 상태 확인</Button> : null}
        {(selectedRequest.target?.resourceType === 'TIME_OFF_REQUEST' ? canCancelTimeOff : !selectedRequest.target && canCancel) ? <Button color="error" variant="outlined" disabled={detailBusy || detailSubmitRetryPending} onClick={() => { if (detailBusy || detailActionPending.current) return; setCancelComment(''); setCancelOpen(true); }}>{selectedRequest.target?.resourceType === 'TIME_OFF_REQUEST' && selectedRequest.status === 'COMPLETED' ? '휴가 취소 요청' : '요청 취소'}</Button> : null}
        {!selectedRequest.target && canCreate && selectedRequest.status === 'NEEDS_CHANGES' && canReadTemplates && !detailSubmitRetryPending ? <Button variant="contained" disabled={detailBusy} onClick={() => openDraft(selectedRequest)}>보완해서 다시 제출</Button>
          : !selectedRequest.target && canCreate && ['DRAFT', 'NEEDS_CHANGES'].includes(selectedRequest.status) ? <Button variant="contained" disabled={detailBusy || selectedRequest.draftVersion === undefined} onClick={() => void submitRequest()}>{detailSubmitSaving ? '제출 중…' : detailSubmitRetryPending ? '제출 다시 시도' : selectedRequest.status === 'NEEDS_CHANGES' ? '재제출' : '제출'}</Button> : null}
      </> : undefined}
    /> : null}
    <FormDialog open={cancelOpen} onClose={() => { if (!detailCancelSaving) setCancelOpen(false); }} onSubmit={cancelRequest} busy={detailCancelSaving} title={selectedRequest?.status === 'DRAFT' ? '초안을 폐기할까요?' : cancellingCompletedTimeOff ? '휴가 취소를 요청할까요?' : '요청을 취소할까요?'} cancelLabel="돌아가기" submitLabel={selectedRequest?.status === 'DRAFT' ? '초안 폐기' : cancellingCompletedTimeOff ? '취소 요청' : '요청 취소'} submitColor="error" submitDisabled={selectedRequest?.status !== 'DRAFT' && !cancelComment.trim()}>
      <Typography variant="body2">{selectedRequest ? workflowRequestTitle(selectedRequest) : ''}</Typography>
      <Typography variant="body2" color="text.secondary">{selectedRequest?.status === 'DRAFT' ? '폐기한 초안은 다시 제출할 수 없습니다.' : cancellingCompletedTimeOff ? '취소 승인이 필요한 휴가는 승인 후 취소됩니다. 승인 없이 처리되는 휴가는 바로 취소됩니다. 취소 사유는 이력에 남습니다.' : '진행 중인 결재가 중단됩니다. 취소 사유는 요청 이력에 남습니다.'}</Typography>
      <FormTextField label="취소 사유" required={selectedRequest?.status !== 'DRAFT'} value={cancelComment} onChange={(event) => setCancelComment(event.target.value)} multiline minRows={2} disabled={detailCancelSaving} slotProps={{ htmlInput: { maxLength: 2000 } }} />
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
    </FormDialog>
    {createOpen && canCreate ? <WorkflowRequestComposer
      templates={availableTemplates}
      initialTemplateId={createTemplateId}
      editing={editingRequest}
      requesterWorkerId={requesterWorkerId}
      presentation={composerPresentation}
      onPresentationChange={changeComposerPresentation}
      onClose={closeComposer}
      onDraftPersisted={rememberComposerDraft}
      onSubmissionResolved={(request) => {
        const context = composerReturnContext.current;
        composerReturnContext.current = undefined;
        activeComposerDraftID.current = undefined;
        setRouteDraftID(undefined);
        setCreateOpen(false); setEditingRequest(undefined); setCreateTemplateId(undefined);
        requests.reload(); onRequestsChanged?.();
        setRequestNotice('최신 요청 상태를 확인했습니다.');
        route.navigate('my-workflow-requests', workflowRequestRouteSearch(context?.status ?? requestStatus, request.id, context?.search ?? requestQuery, context?.filters ?? requestFilters));
      }}
      onComplete={(action, requestId, fixtureRequest) => {
        if (fixtureMode && fixtureRequest) rememberFixtureRequest(fixtureRequest);
        activeComposerDraftID.current = undefined;
        setCreateOpen(false); setEditingRequest(undefined); setCreateTemplateId(undefined);
        finishRequestAction(action === 'draft'
          ? '요청을 임시저장했습니다. 내 요청에서 이어서 작성할 수 있습니다.'
          : canReadRequests
            ? '요청을 제출했습니다. 내 요청에서 진행 상태를 확인할 수 있습니다.'
            : '요청을 제출했습니다.', canReadRequests, action === 'submit' ? { requestId } : undefined);
      }}
      port={port}
      approvalSettingsPort={approvalSettingsPort}
      directoryPort={directoryPort}
      fixtureMode={fixtureMode}
      canCreate={canCreate}
    /> : null}
  </>;
}

function workflowTemplateCategory(template: WorkflowTemplate) {
  return template.category?.trim() || '기타';
}

function WorkflowTemplateCard({ template, disabled, onClick }: { template: WorkflowTemplate; disabled: boolean; onClick: () => void }) {
  const category = workflowTemplateCategory(template);
  const iconKey = resolveWorkflowTemplateIconKey(template);
  const visual = resolveWorkflowTemplateIconVisual(template);
  const categoryID = `workflow-template-${template.id}-category`;
  const detailsID = `workflow-template-${template.id}-details`;
  return <InteractiveCard
    disabled={disabled}
    aria-label={`${template.displayName} 작성 시작`}
    aria-describedby={template.version.description ? `${categoryID} ${detailsID}` : categoryID}
    onClick={onClick}
    sx={{
      height: '100%', minHeight: { xs: 140, sm: 148 }, p: { xs: `${designTokens.layout.surfacePaddingCompact}px`, sm: `${designTokens.layout.surfacePadding}px` },
    }}
  >
    <Stack spacing={`${designTokens.space.sm}px`} sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box aria-hidden data-testid={`workflow-template-card-icon-${iconKey.toLowerCase()}`} sx={{ width: { xs: 42, sm: 48 }, height: { xs: 42, sm: 48 }, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.surface, bgcolor: visual.surface, color: visual.color, '& svg': { fontSize: { xs: 23, sm: 26 } } }}>{visual.icon}</Box>
        <ValueBadge
          id={categoryID}
          label={category}
          maxWidth="calc(100% - 56px)"
        />
      </Stack>
      <Box sx={{ minWidth: 0, display: 'grid', gap: `${designTokens.space.sm}px` }}>
        <Typography component="h3" sx={{ ...designTokens.typography.componentTitle, wordBreak: 'keep-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{template.displayName}</Typography>
        {template.version.description && <Box id={detailsID}>
          <Typography color="text.secondary" sx={{ ...designTokens.typography.ui, fontWeight: 400, wordBreak: 'keep-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{template.version.description}</Typography>
        </Box>}
      </Box>
    </Stack>
  </InteractiveCard>;
}

function WorkflowRequestListItem({ request, onClick, onResume }: { request: WorkflowRequest; onClick: () => void; onResume?: () => void }) {
  const title = workflowRequestTitle(request);
  const templateIconKey = request.templateIconKey ?? 'DOCUMENT';
  const templateVisual = workflowTemplateIconVisual(templateIconKey);
  return <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', borderRadius: 'inherit' }}><ButtonBase
    data-slot="request-detail"
    aria-label={`${title} 요청 상세 열기, ${request.templateName}, 상태 ${statusLabel[workflowRequestDisplayStatus(request)] ?? request.status}, ${formatWorkflowDate(request.updatedAt)} 업데이트`}
    onClick={onClick}
    disableRipple
    sx={{
      flex: 1, minWidth: 180, p: { xs: 1.5, sm: 1.75 }, gap: { xs: 1, sm: 2 }, textAlign: 'left',
      display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center',
      '&:hover': { bgcolor: designTokens.color.hoverSurface },
      '&:focus-visible, &.Mui-focusVisible': createFocusRingStyles({ inset: true }),
    }}
  >
    <Stack direction="row" spacing={1.25} sx={{ minWidth: 0, alignItems: 'center' }}>
      <Box
        aria-hidden
        data-testid={`workflow-request-list-icon-${templateIconKey.toLowerCase()}`}
        sx={{ width: 40, height: 40, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.surface, bgcolor: templateVisual.surface, color: templateVisual.color, '& svg': { fontSize: 22 } }}
      >{templateVisual.icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 750, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</Typography>
        {request.status === 'IN_PROGRESS' ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
          {request.currentStage ? `${request.currentStage}단계 승인 대기` : '승인 대기'}{request.currentAssignees?.length ? ` · ${request.currentAssignees.map((assignee) => assignee.descriptor || '담당자 확인 불가').join(', ')}` : ''}{request.currentDueAt ? ` · 기한 ${formatWorkflowDateTime(request.currentDueAt)}` : ''}
        </Typography> : request.status === 'NEEDS_CHANGES' ? <Typography variant="body2" color="warning.main" sx={{ mt: 0.25 }}>보완 의견을 확인하고 수정해 주세요.</Typography> : null}
        <Stack direction="row" useFlexGap sx={{ mt: 0.25, columnGap: 0.75, rowGap: 0.25, flexWrap: 'wrap' }}>
          <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere', ...designTokens.typography.supporting }}>{request.templateName}</Typography>
          <Typography component="time" dateTime={request.updatedAt} color="text.secondary" sx={{ whiteSpace: 'nowrap', ...designTokens.typography.supporting }}>{formatWorkflowDate(request.updatedAt)} 업데이트</Typography>
        </Stack>
      </Box>
    </Stack>
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}><RequestStatus request={request} /><ChevronRightOutlined color="action" sx={{ fontSize: 18 }} /></Stack>
  </ButtonBase>{onResume ? <Button variant="outlined" size="small" aria-label={`${title} 이어서 작성`} onClick={onResume} sx={{ m: 1.5 }}>이어서 작성</Button> : null}</Box>;
}
